// client/src/components/Insights/tabs/OverviewTab.jsx
// Health score (with how it is calculated), the plan check against the paycheck
// plan's targets, anomalies, savings opportunities and the balance forecast.
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CardContent, Chip, Grid,
  IconButton, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Skeleton, Stack, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';
import {
  Activity, AlertTriangle, BarChart2, CheckCircle, CreditCard, Eye, PiggyBank, RefreshCw, ShieldCheck,
} from 'lucide-react';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { GlassCard, SectionTitle, ScoreRing } from '../shared/SharedComponents';
import { alpha, gradientTextSx } from '../shared/constants';
import {
  getAllInsights, getBalancePrediction, getBudgetScorecard, getHealthScore, getSavingsOpportunities,
} from '../../../services/insightsAPI';
import { useInsightsFilter } from '../shared/InsightsFilterContext';

const FACTOR_ICONS = {
  budgetAdherence: ShieldCheck,
  savingsRate: PiggyBank,
  spendingConsistency: Activity,
  debtManagement: CreditCard,
  receiptTracking: Eye,
  categoryDiversity: BarChart2,
};

const FACTOR_HOW = {
  budgetAdherence: 'Share of your budgets that stayed within their limit this month.',
  savingsRate: 'Income minus spending; saving 20% of income or more is full marks.',
  spendingConsistency: 'How steady your monthly spending has been over recent months.',
  debtManagement: 'Share of your split bills that are settled.',
  receiptTracking: 'Receipts scanned compared with the number of expenses.',
  categoryDiversity: 'How evenly spending spreads across categories instead of piling into one.',
};

const BUCKETS = [
  { key: 'needs', color: '#0f766e', label: 'Needs' },
  { key: 'wants', color: '#b45309', label: 'Wants' },
  { key: 'savings', color: '#1d4ed8', label: 'Savings' },
];

const scoreColor = (s) => (s >= 70 ? '#10b981' : s >= 40 ? '#f59e0b' : '#ef4444');

function PlanBar({ pct, target, color, isSavings }) {
  const max = Math.max(100, pct, target);
  const off = isSavings ? pct < target - 2 : pct > target + 2;
  return (
    <Box sx={{ position: 'relative', height: 10, borderRadius: 99, bgcolor: alpha('#9ca3af', 0.15), overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', inset: 0, width: `${Math.min(100, (Math.max(0, pct) / max) * 100)}%`, bgcolor: off ? '#ef4444' : color }} />
      <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(target / max) * 100}%`, width: 2, bgcolor: 'text.primary', opacity: 0.5 }} />
    </Box>
  );
}

function PlanCheck({ data }) {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const navigate = useNavigate();
  if (!data) return null;
  const tipText = (tip) => t(`aiInsights.plan.tips.${tip.code}`, {
    pct: tip.pct,
    target: tip.target,
    amount: tip.amount != null ? formatAmount(tip.amount) : '',
    defaultValue: {
      needsHigh: 'Needs take {{pct}}% of income; your plan allows {{target}}%. Fixed costs are the place to look.',
      wantsHigh: 'Wants take {{pct}}% of income; your plan allows {{target}}%. That is about {{amount}} a month over.',
      savingsLow: 'You save {{pct}}% of income; aim for {{target}}%.',
      unsorted: '{{amount}} a month has no category. Sort it so this check is complete.',
      onTrack: 'Your typical month fits your plan.',
      noIncome: 'No income recorded in the last 3 months, so shares of income cannot be worked out.',
      noHistory: 'Not enough history yet. Come back after a full month of tracking.',
    }[tip.code] || '',
  });
  const codes = Array.isArray(data.tipCodes) ? data.tipCodes : (data.tips || []).filter((x) => x && typeof x === 'object');

  return (
    <GlassCard sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} sx={{ mb: 1.5 }}>
          <Box>
            <Typography fontWeight={800}>{t('aiInsights.plan.title', 'Your month against your plan')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('aiInsights.plan.lead', { count: data.window?.months || 0, defaultValue: 'A typical month from your last {{count}} full months, against your paycheck plan targets.' })}
            </Typography>
          </Box>
          {data.grade && <Chip label={data.grade} sx={{ fontWeight: 900, bgcolor: alpha(scoreColor(data.score), 0.15), color: scoreColor(data.score) }} />}
        </Stack>
        {data.hasIncome && (
          <Stack spacing={1.5}>
            {BUCKETS.map((b) => {
              const row = data[b.key] || {};
              return (
                <Box key={b.key}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: b.color }}>{t(`paycheck.bucket.${b.key}`, b.label)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('aiInsights.plan.row', { pct: row.pct, target: row.target, amount: formatAmount(row.amount || 0), defaultValue: '{{pct}}% · target {{target}}% · {{amount}}/month' })}
                    </Typography>
                  </Stack>
                  <PlanBar pct={row.pct || 0} target={row.target || 0} color={b.color} isSavings={b.key === 'savings'} />
                </Box>
              );
            })}
          </Stack>
        )}
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          {codes.map((tip) => (
            <Typography key={tip.code} variant="body2" color={tip.code === 'onTrack' ? 'success.main' : 'text.primary'}>{tipText(tip)}</Typography>
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {data.targets?.fromPlan
              ? t('aiInsights.plan.fromPlan', 'Targets come from your paycheck plan and your city cost.')
              : t('aiInsights.plan.defaultTargets', 'Using mid-cost city targets. Build a paycheck plan for targets that fit your city.')}
          </Typography>
          <Button size="small" onClick={() => navigate('/dashboard/paycheck')}>{t('aiInsights.plan.open', 'Open the paycheck plan')}</Button>
        </Stack>
      </CardContent>
    </GlassCard>
  );
}

function HealthScore({ data, onRefresh }) {
  const { t, i18n } = useTranslation();
  const { formatAmount } = useCurrency();
  if (!data) return null;
  const score = data.score || 0;
  const active = (data.breakdown || []).filter((b) => b.hasData !== false);
  const totalWeight = active.reduce((s, b) => s + (b.weight || 0), 0) || 1;
  const monthName = data.facts?.monthIndex != null && data.year
    ? new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' }).format(new Date(data.year, data.facts.monthIndex, 1))
    : `${data.month || ''} ${data.year || ''}`.trim();
  const summary = data.facts
    ? (data.facts.transactions > 0
      ? t('aiInsights.health.summary', {
        month: monthName,
        income: formatAmount(data.facts.income),
        expenses: formatAmount(data.facts.expenses),
        count: data.facts.transactions,
        defaultValue: 'In {{month}} you received {{income}} and spent {{expenses}} across {{count}} transactions.',
      })
      : t('aiInsights.health.noTransactions', { month: monthName, defaultValue: 'No transactions recorded in {{month}}.' }))
    : data.narrative;

  return (
    <GlassCard sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography fontWeight={800}>{t('aiInsights.health.title', { month: monthName, defaultValue: 'Financial health · {{month}}' })}</Typography>
          <IconButton size="small" onClick={onRefresh} aria-label={t('aiInsights.common.retry', 'Retry')}><RefreshCw size={14} /></IconButton>
        </Stack>
        <ScoreRing score={score} size={150} strokeWidth={10} />
        {summary && <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1.5 }}>{summary}</Typography>}
        {!!active.length && (
          <Grid container spacing={1.5} sx={{ mt: 1 }}>
            {active.map((b) => {
              const Icon = FACTOR_ICONS[b.key] || Activity;
              const c = scoreColor(b.score);
              return (
                <Grid item xs={6} md={4} key={b.key}>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(c, 0.06) }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Icon size={14} color={c} />
                      <Typography variant="caption" fontWeight={700}>{t(`aiInsights.overview.subScores.${b.key}`, b.factor)}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <Typography variant="h6" fontWeight={900} sx={{ color: c }}>{b.score}</Typography>
                      <LinearProgress variant="determinate" value={b.score} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: alpha(c, 0.12), '& .MuiLinearProgress-bar': { bgcolor: c } }} />
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
        {!!active.length && (
          <Accordion disableGutters elevation={0} sx={{ mt: 1.5, bgcolor: 'transparent', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography variant="body2" fontWeight={700}>{t('aiInsights.health.how', 'How this score is calculated')}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              <Stack spacing={1}>
                {active.map((b) => (
                  <Box key={b.key}>
                    <Typography variant="body2" fontWeight={700}>
                      {t(`aiInsights.overview.subScores.${b.key}`, b.factor)} · {t('aiInsights.health.weight', { pct: Math.round(((b.weight || 0) / totalWeight) * 100), defaultValue: '{{pct}}% of the score' })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{t(`aiInsights.health.factor.${b.key}`, FACTOR_HOW[b.key] || '')}</Typography>
                  </Box>
                ))}
                <Typography variant="caption" color="text.secondary">
                  {t('aiInsights.health.missing', 'Parts with no data this month are left out and the others count for more.')}
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </GlassCard>
  );
}

const RELATED = [
  { path: '/dashboard/goals', key: 'goals', label: 'Savings goals' },
  { path: '/dashboard/cashflow', key: 'cashflow', label: 'Cash-flow forecast' },
  { path: '/dashboard/merchants', key: 'merchants', label: 'Merchants' },
  { path: '/dashboard/paycheck', key: 'whatIf', label: 'What-ifs in the paycheck plan' },
];

const OverviewTab = () => {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { from, to } = useInsightsFilter();

  const health = useQuery({ queryKey: ['insightsHealthScore', from, to], queryFn: () => getHealthScore({ from, to }), select: (d) => d?.data || d });
  const plan = useQuery({ queryKey: ['insightsPlanCheck'], queryFn: () => getBudgetScorecard({}), select: (d) => d?.data || d });
  const insights = useQuery({ queryKey: ['insightsAll', from, to], queryFn: () => getAllInsights({ from, to }), select: (d) => d?.data || d });
  const prediction = useQuery({ queryKey: ['insightsPrediction30', from, to], queryFn: () => getBalancePrediction({ from, to, days: 30 }), select: (d) => d?.data || d });
  const opportunities = useQuery({ queryKey: ['insightsSavings', from, to], queryFn: () => getSavingsOpportunities({ from, to }), select: (d) => d?.data || d });

  const refetchAll = () => {
    ['insightsHealthScore', 'insightsPlanCheck', 'insightsAll', 'insightsPrediction30', 'insightsSavings']
      .forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));
  };

  if (health.isLoading && plan.isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={160} sx={{ borderRadius: 3 }} />
      </Stack>
    );
  }

  if (!health.data && !plan.data && !insights.data && !health.isLoading) {
    return (
      <Alert severity="warning" action={<Button onClick={refetchAll} size="small">{t('aiInsights.common.retry', 'Retry')}</Button>}>
        {t('aiInsights.overview.loadError', 'Could not load your overview.')}
      </Alert>
    );
  }

  const anomalies = insights.data?.anomalies || [];
  const predData = (prediction.data?.predictions || []).map((p) => ({ day: p.day, balance: p.predictedBalance }));
  const opps = opportunities.data?.opportunities || [];
  const tips = (health.data?.tips || []).slice(0, 3);
  const planOk = plan.data?.score != null && plan.data.score >= 80;

  const wins = [];
  if (planOk) wins.push(t('aiInsights.overview.winsList.plan', 'Your typical month fits your plan.'));
  if (!insights.isLoading && insights.data && anomalies.length === 0) wins.push(t('aiInsights.overview.winsList.noAnomalies', 'No unusual spending in this period.'));
  if ((health.data?.score || 0) >= 60) wins.push(t('aiInsights.overview.winsList.healthy', 'Your health score is 60 or above.'));

  return (
    <Box>
      <SectionTitle icon={Activity}>{t('aiInsights.overview.title', 'Financial Health Dashboard')}</SectionTitle>

      {health.isLoading ? <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3, mb: 3 }} /> : <HealthScore data={health.data} onRefresh={refetchAll} />}

      {plan.isLoading ? <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3, mb: 3 }} /> : <PlanCheck data={plan.data} />}

      {(wins.length > 0 || tips.length > 0) && (
        <Grid container spacing={2} mb={3}>
          {wins.length > 0 && (
            <Grid item xs={12} md={6}>
              <GlassCard sx={{ borderTop: '3px solid #10b981', height: '100%' }}>
                <CardContent>
                  <Typography fontWeight={800} mb={1.5} color="#10b981">{t('aiInsights.overview.winsTitle', 'Going well')}</Typography>
                  {wins.map((w) => (
                    <Box key={w} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <CheckCircle size={16} color="#10b981" style={{ marginTop: 2, flexShrink: 0 }} />
                      <Typography variant="body2">{w}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </GlassCard>
            </Grid>
          )}
          {tips.length > 0 && (
            <Grid item xs={12} md={6}>
              <GlassCard sx={{ borderTop: '3px solid #f59e0b', height: '100%' }}>
                <CardContent>
                  <Typography fontWeight={800} mb={1.5} color="#f59e0b">{t('aiInsights.overview.improveTitle', 'Worth a look')}</Typography>
                  {tips.map((tip) => (
                    <Box key={tip} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <AlertTriangle size={16} color="#f59e0b" style={{ marginTop: 2, flexShrink: 0 }} />
                      <Typography variant="body2">{tip}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </GlassCard>
            </Grid>
          )}
        </Grid>
      )}

      <GlassCard sx={{ mb: 3 }}>
        <CardContent>
          <Typography fontWeight={800} mb={1}>{t('aiInsights.overview.anomalyRadar', 'Anomaly Radar')}</Typography>
          {insights.isLoading ? <Skeleton height={60} /> : anomalies.length === 0 ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircle size={18} color="#10b981" />
              <Typography color="text.secondary">{t('aiInsights.overview.noAnomaliesPeriod', 'No unusual spending in this period.')}</Typography>
            </Stack>
          ) : (
            <List dense>
              {anomalies.slice(0, 5).map((a, i) => (
                <ListItem key={i} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><AlertTriangle size={16} color="#f59e0b" /></ListItemIcon>
                  <ListItemText primary={a.description || a.name} secondary={a.amount ? formatAmount(a.amount) : ''} />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </GlassCard>

      {predData.length > 0 && (
        <GlassCard sx={{ mb: 3 }}>
          <CardContent>
            <Typography fontWeight={800} mb={2} sx={gradientTextSx}>{t('aiInsights.overview.balanceForecast', '30-Day Balance Forecast')}</Typography>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={predData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatAmount(v)} width={80} />
                <RTooltip formatter={(v) => formatAmount(v)} />
                <Area type="monotone" dataKey="balance" stroke="#8b5cf6" fill={alpha('#8b5cf6', 0.12)} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </GlassCard>
      )}

      {opps.length > 0 && (
        <GlassCard sx={{ mb: 3 }}>
          <CardContent>
            <Typography fontWeight={800} mb={1} sx={gradientTextSx}>{t('aiInsights.overview.savingsOpportunities', '💡 Savings Opportunities')}</Typography>
            <List dense>
              {opps.slice(0, 6).map((op, i) => (
                <ListItem key={i} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}><PiggyBank size={16} color="#10b981" /></ListItemIcon>
                  <ListItemText
                    primary={op.category}
                    secondary={t('aiInsights.overview.perMonth', { amount: formatAmount(op.monthly_saving || 0), defaultValue: 'Save {{amount}} a month' })}
                  />
                  <Typography fontWeight={800} color="#10b981" fontSize={14}>
                    {t('aiInsights.overview.perYear', { amount: formatAmount(op.annual_saving || 0), defaultValue: '{{amount}} a year' })}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </GlassCard>
      )}

      <GlassCard>
        <CardContent>
          <Typography fontWeight={800} mb={1}>{t('aiInsights.overview.relatedTitle', 'More tools')}</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {RELATED.map((r) => (
              <Button key={r.key} variant="outlined" size="small" onClick={() => navigate(r.path)}>
                {t(`aiInsights.overview.related.${r.key}`, r.label)}
              </Button>
            ))}
          </Stack>
        </CardContent>
      </GlassCard>
    </Box>
  );
};

export default OverviewTab;
